import BlockUtility from './block-utility';
import {type CachedBlockData, getCached as getCachedExecuteBlock} from './blocks-execute-cache';
import log from '../util/log';
import Thread from './thread';
import {Map} from 'immutable';
import cast from '../util/cast';
import type Blocks from './blocks';
import type {VMField, VMInput, VMMutation} from '../serialization/schema';
import type Profiler from './profiler';
import type {ProfilerFrame} from './profiler';
import type Sequencer from './sequencer';
import type {BlockFunction} from '../blocks/category_prototype';

/**
 * Single BlockUtility instance reused by execute for every pritimive ran.
 * @constant
 */
const blockUtility = new BlockUtility();

/**
 * Profiler frame name for block functions.
 * @constant
 */
const blockFunctionProfilerFrame = 'blockFunction' as const;

/**
 * Profiler frame ID for 'blockFunction'.
 */
let blockFunctionProfilerId = -1;

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Utility function to determine if a value is a Promise.
 * @param value Value to check for a Promise.
 * @returns True if the value appears to be a Promise.
 */
const isPromise = function (value: any): value is Promise<unknown> {
    return (
        value !== null &&
        typeof value === 'object' &&
        typeof value.then === 'function'
    );
};
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * Utility function to determine if a block is a procedure caller.
 * @param cached Cached block to check.
 * @returns True if the block is a procedure.
 */
const isProcedureCaller = function (cached: BlockCached) {
    return cached.opcode === 'procedures_call';
};

type VariableFieldKeys = 'VARIABLE' | 'LIST' | 'BROADCAST_OPTION';

type ArgValues = {
    mutation?: VMMutation;
    [key: string]: CachedArgValue | VMMutation;
} & {
    [key in VariableFieldKeys]?: VariableArgValue;
};

interface VariableArgValue {
    id: string | null;
    name: string | null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CachedArgValue = any;

/**
 * Handle any reported value from the primitive, either directly returned
 * or after a promise resolves.
 * @param resolvedValue Value eventually returned from the primitive.
 * @param sequencer Sequencer stepping the thread for the ran
 * primitive.
 * @param thread Thread containing the primitive.
 * @param blockCached Cached block metadata.
 * @param lastOperation True if this is the last operation in a stack.
 */
// @todo move this to callback attached to the thread when we have performance
// metrics (dd)
const handleReport = function (
    resolvedValue: unknown,
    sequencer: Sequencer,
    thread: Thread,
    blockCached: BlockCached,
    lastOperation: boolean
) {
    const currentBlockId = blockCached.id;
    const opcode = blockCached.opcode;
    const isHat = blockCached._isHat;

    thread.pushReportedValue(resolvedValue);
    if (isHat) {
        // Hat predicate was evaluated.
        if (sequencer.runtime.getIsEdgeActivatedHat(opcode)) {
            // If this is an edge-activated hat, only proceed if the value is
            // true and used to be false, or the stack was activated explicitly
            // via stack click
            if (!thread.stackClick) {
                const hasOldEdgeValue = thread.target!.hasEdgeActivatedValue(currentBlockId);
                const oldEdgeValue = thread.target!.updateEdgeActivatedValue(
                    currentBlockId,
                    resolvedValue
                );

                const edgeWasActivated = hasOldEdgeValue ? (!oldEdgeValue && resolvedValue) : resolvedValue;
                if (!edgeWasActivated) {
                    sequencer.retireThread(thread);
                }
            }
        } else if (!resolvedValue) {
            // Not an edge-activated hat: retire the thread
            // if predicate was false.
            sequencer.retireThread(thread);
        }
    } else {
        // In a non-hat, report the value visually if necessary if
        // at the top of the thread stack.
        if (lastOperation && typeof resolvedValue !== 'undefined' && thread.atStackTop()) {
            if (thread.stackClick) {
                sequencer.runtime.visualReport(currentBlockId, resolvedValue);
            }
            if (thread.updateMonitor) {
                const targetId = sequencer.runtime.monitorBlocks.getBlock(currentBlockId)!.targetId;
                if (targetId && !sequencer.runtime.getTargetById(targetId)) {
                    // Target no longer exists
                    return;
                }
                sequencer.runtime.requestUpdateMonitor(Map({
                    id: currentBlockId,
                    spriteName: targetId ? sequencer.runtime.getTargetById(targetId)!.getName() : null,
                    value: resolvedValue
                }));
            }
        }
        // Finished any yields.
        thread.status = Thread.STATUS_RUNNING;
    }
};

/**
 * Handle a value that may be a Promise returned from a primitive, yielding the thread if so.
 * @param primitiveReportedValue Value returned from the primitive, which may be a Promise.
 * @param sequencer Sequencer stepping the thread for the ran primitive.
 * @param thread Thread containing the primitive.
 * @param blockCached Cached block metadata.
 * @param lastOperation True if this is the last operation in a stack.
 */
const handlePromise = (
    primitiveReportedValue: Promise<unknown>,
    sequencer: Sequencer,
    thread: Thread,
    blockCached: BlockCached,
    lastOperation: boolean
) => {
    if (thread.status === Thread.STATUS_RUNNING) {
        // Primitive returned a promise; automatically yield thread.
        thread.status = Thread.STATUS_PROMISE_WAIT;
    }
    // Promise handlers
    primitiveReportedValue.then(resolvedValue => {
        handleReport(resolvedValue, sequencer, thread, blockCached, lastOperation);
        // If it's a command block or a top level reporter in a stackClick.
        if (lastOperation) {
            let stackFrame;
            let nextBlockId;
            let target;
            do {
                // In the case that the promise is the last block in the current thread stack
                // We need to pop out repeatedly until we find the next block.
                const willPop = thread.peekStack();
                if (willPop === null) {
                    return;
                }
                nextBlockId = thread.blockContainer!.getNextBlock(willPop);
                target = thread.peekStackFrame()!.target!;
                thread.popStack();
                if (nextBlockId !== null) {
                    // A next block exists so break out this loop
                    break;
                }
                // Investigate the next block and if not in a loop,
                // then repeat and pop the next item off the stack frame
                stackFrame = thread.peekStackFrame();
            } while (stackFrame !== null && !stackFrame.isLoop && !stackFrame.waitingReporter);

            thread.pushStack(nextBlockId, target);
        }
    }, rejectionReason => {
        // Promise rejected: the primitive had some error.
        // Log it and proceed.
        log.warn('Primitive rejected promise: ', rejectionReason);
        thread.status = Thread.STATUS_RUNNING;
        thread.popStack();
    });
};

/**
 * A execute.js internal representation of a block to reduce the time spent in
 * execute as the same blocks are called the most.
 *
 * With the help of the Blocks class create a mutable copy of block
 * information. The members of BlockCached derived values of block information
 * that does not need to be reevaluated until a change in Blocks. Since Blocks
 * handles where the cache instance is stored, it drops all cache versions of a
 * block when any change happens to it. This way we can quickly execute blocks
 * and keep perform the right action according to the current block information
 * in the editor.
 */
class BlockCached {
    /**
     * Block id in its parent set of blocks.
     */
    id: string;
    /**
     * Block operation code for this block.
     */
    opcode: string;

    /**
     * Original block object containing argument values for static fields.
     */
    fields: Record<string, VMField>;

    /**
     * Original block object containing argument values for executable inputs.
     */
    inputs: Record<string, VMInput>;

    /**
     * Procedure mutation.
     */
    mutation?: VMMutation;
    /**
     * The profiler the block is configured with.
     */
    _profiler: Profiler | null = null;

    /**
     * Profiler information frame.
     */
    _profilerFrame: ProfilerFrame | null = null;

    /**
     * Is the opcode a hat (event responder) block.
     */
    _isHat = false;

    /**
     * The block opcode's implementation function.
     */
    _blockFunction?: BlockFunction | null = null;

    /**
     * Is the block function defined for this opcode?
     */
    protected _definedBlockFunction = false;

    /**
     * Is this block a block with no function but a static value to return.
     */
    protected _isShadowBlock = false;

    /**
     * The static value of this block if it is a shadow block.
     */
    /* eslint-disable @typescript-eslint/no-explicit-any */
    protected _shadowValue: any = null;
    /* eslint-enable @typescript-eslint/no-explicit-any */

    /**
     * A copy of the block's fields that may be modified.
     */
    protected _fields: Record<string, VMField>;
    /**
     * A copy of the block's inputs that may be modified.
     */
    protected _inputs: Record<string, VMInput>;

    /**
     * The inputs key the parent refers to this BlockCached by.
     */
    _parentKey: string | null = null;

    /**
     * The target object where the parent wants the resulting value stored
     * with _parentKey as the key.
     */
    _parentValues: ArgValues | null = null;

    /**
     * An arguments object for block implementations. All executions of this
     * specific block will use this objecct.
     */
    _argValues: ArgValues;

    /**
     * A sequence of non-shadow operations that can must be performed. This
     * list recreates the order this block and its children are executed.
     * Since the order is always the same we can safely store that order
     * and iterate over the operations instead of dynamically walking the
     * tree every time.
     */
    _ops: BlockCached[] = [];

    /**
     * @param blockContainer the related Blocks instance
     * @param cached default set of cached values
     */
    constructor (blockContainer: Blocks, cached: CachedBlockData) {
        this.id = cached.id;
        this.opcode = cached.opcode;
        this.fields = cached.fields;
        this.inputs = cached.inputs;
        this.mutation = cached.mutation;

        this._fields = Object.assign({}, this.fields);
        this._inputs = Object.assign({}, this.inputs);
        this._argValues = {
            mutation: this.mutation
        };

        const {runtime} = blockUtility.sequencer;

        const {opcode, fields, inputs} = this;

        // Assign opcode isHat and blockFunction data to avoid dynamic lookups.
        this._isHat = runtime.getIsHat(opcode);
        this._blockFunction = runtime.getOpcodeFunction(opcode);
        this._definedBlockFunction = typeof this._blockFunction !== 'undefined';

        // Store the current shadow value if there is a shadow value.
        const fieldKeys = Object.keys(fields);
        this._isShadowBlock = (
            !this._definedBlockFunction &&
            fieldKeys.length === 1 &&
            Object.keys(inputs).length === 0
        );
        this._shadowValue = this._isShadowBlock && fields[fieldKeys[0]].value;

        // Store the static fields onto _argValues.
        for (const fieldName in fields) {
            if (
                fieldName === 'VARIABLE' ||
                fieldName === 'LIST' ||
                fieldName === 'BROADCAST_OPTION'
            ) {
                this._argValues[fieldName] = {
                    id: fields[fieldName].id!,
                    name: fields[fieldName].value!
                };
            } else {
                this._argValues[fieldName] = fields[fieldName].value;
            }
        }

        // Remove custom_block. It is not part of block execution.
        delete this._inputs.custom_block;

        if ('BROADCAST_INPUT' in this._inputs) {
            // BROADCAST_INPUT is called BROADCAST_OPTION in the args and is an
            // object with an unchanging shape.
            this._argValues.BROADCAST_OPTION = {
                id: null,
                name: null
            };

            // We can go ahead and compute BROADCAST_INPUT if it is a shadow
            // value.
            const broadcastInput = this._inputs.BROADCAST_INPUT;
            if (broadcastInput.block === broadcastInput.shadow) {
                // Shadow dropdown menu is being used.
                // Get the appropriate information out of it.
                const shadow = blockContainer.getBlock(broadcastInput.shadow)!;
                const broadcastField = shadow.fields.BROADCAST_OPTION;
                this._argValues.BROADCAST_OPTION.id = broadcastField.id!;
                this._argValues.BROADCAST_OPTION.name = broadcastField.value!;

                // Evaluating BROADCAST_INPUT here we do not need to do so
                // later.
                delete this._inputs.BROADCAST_INPUT;
            }
        }

        const order = runtime.getExecutionOrders(opcode);

        if (order) {
            // Cache all inputs with given order.
            for (const item of order) {
                if (typeof item === 'object') {
                    if (item.execute) {
                        if (item.execute === this.opcode) {
                            this._ops.push(this);
                        } else {

                            const newCached = new BlockCached(blockContainer, {
                                id: '',
                                opcode: item.execute,
                                fields: {},
                                inputs: {}
                            });
                            newCached._argValues = this._argValues;
                            newCached._parentValues = {};
                            this._ops.push(newCached);
                        }
                    }
                } else if (Object.prototype.hasOwnProperty.call(this._inputs, item)) {
                    this._pushInput(item, blockContainer);
                }
            }
        } else {
            // Cache all input children blocks in the operation lists. The
            // operations can later be run in the order they appear in correctly
            // executing the operations quickly in a flat loop instead of needing to
            // recursivly iterate them.
            for (const inputName in this._inputs) {
                this._pushInput(inputName, blockContainer);
            }

            // The final operation is this block itself. At the top most block is a
            // command block or a block that is being run as a monitor.
            if (this._definedBlockFunction) {
                this._ops.push(this);
            }
        }
    }

    /**
     * Push an input with given name to ops.
     * @param inputName The input name.
     * @param blockContainer The related Blocks instance.
     */
    protected _pushInput (inputName: string, blockContainer: Blocks) {
        const input = this._inputs[inputName];
        if (input.block) {
            const inputCached = getCachedExecuteBlock(blockContainer, input.block, BlockCached)!;
            if (inputCached._isHat) {
                return;
            }

            this._ops.push(...inputCached._ops);
            inputCached._parentKey = inputName;
            inputCached._parentValues = this._argValues;

            // Shadow values are static and do not change, go ahead and
            // store their value on args.
            if (inputCached._isShadowBlock) {
                this._argValues[inputName] = inputCached._shadowValue;
            }
        }
    }
}

/**
 * Initialize a BlockCached instance so its command/hat
 * block and reporters can be profiled during execution.
 * @param profiler - The profiler that is currently enabled.
 * @param blockCached - The blockCached instance to profile.
 */
const _prepareBlockProfiling = function (profiler: Profiler, blockCached: BlockCached) {
    blockCached._profiler = profiler;

    if (blockFunctionProfilerId === -1) {
        blockFunctionProfilerId = profiler.idByName(blockFunctionProfilerFrame);
    }

    const ops = blockCached._ops;
    for (let i = 0; i < ops.length; i++) {
        ops[i]._profilerFrame = profiler.frame(blockFunctionProfilerId, ops[i].opcode);
    }
};

/**
 * Execute a block.
 * @param sequencer Which sequencer is executing.
 * @param thread Thread which to read and execute.
 */
const execute = function (sequencer: Sequencer, thread: Thread) {
    const runtime = sequencer.runtime;

    // store sequencer and thread so block functions can access them through
    // convenience methods.
    blockUtility.sequencer = sequencer;
    blockUtility.thread = thread;

    // Current block to execute is the one on the top of the stack.
    const currentBlockId = thread.peekStack()!;
    const currentStackFrame = thread.peekStackFrame()!;

    let blockContainer = thread.blockContainer!;
    let blockCached = getCachedExecuteBlock(blockContainer, currentBlockId, BlockCached);
    if (blockCached === null) {
        blockContainer = runtime.flyoutBlocks;
        blockCached = getCachedExecuteBlock(blockContainer, currentBlockId, BlockCached);
        // Stop if block or target no longer exists.
        if (blockCached === null) {
            // No block found: stop the thread; script no longer exists.
            sequencer.retireThread(thread);
            return;
        }
    }

    const ops = blockCached._ops;
    const length = ops.length;
    let i = 0;

    if (currentStackFrame.reported !== null) {
        const reported = currentStackFrame.reported;
        // Reinstate all the previous values.
        for (; i < reported.length; i++) {
            const {opCached: oldOpCached, inputValue} = reported[i];

            const opCached = ops.find(op => op.id === oldOpCached);

            if (opCached) {
                const inputName = opCached._parentKey!;
                const argValues = opCached._parentValues!;

                if (inputName === 'BROADCAST_INPUT') {
                    // Something is plugged into the broadcast input.
                    // Cast it to a string. We don't need an id here.
                    argValues.BROADCAST_OPTION!.id = null;
                    argValues.BROADCAST_OPTION!.name = cast.toString(inputValue);
                } else {
                    argValues[inputName] = inputValue;
                }
            }
        }

        // Find the last reported block that is still in the set of operations.
        // This way if the last operation was removed, we'll find the next
        // candidate. If an earlier block that was performed was removed then
        // we'll find the index where the last operation is now.
        if (reported.length > 0) {
            const lastExisting = reported.reverse().find(report => ops.find(op => op.id === report.opCached));
            if (lastExisting) {
                i = ops.findIndex(opCached => opCached.id === lastExisting.opCached) + 1;
            } else {
                i = 0;
            }
        }

        // The reporting block must exist and must be the next one in the sequence of operations.
        if (ops[i] && ops[i].id === currentStackFrame.reporting) {
            const opCached = ops[i];
            const inputValue = thread.justReported;

            thread.justReported = null;

            const inputName = opCached._parentKey!;
            const argValues = opCached._parentValues!;

            // cc - if current call is the last operation, which means that it is called by clicking directly,
            // then call handleReport.
            if (currentStackFrame.waitingReporter && i === length - 1) {
                // cc - if returned value is null, then set the argument to undefined to avoid visual report.
                handleReport(inputValue ?? undefined, sequencer, thread, opCached, true);
            } else if (inputName === 'BROADCAST_INPUT') {
                // Something is plugged into the broadcast input.
                // Cast it to a string. We don't need an id here.
                argValues.BROADCAST_OPTION!.id = null;
                argValues.BROADCAST_OPTION!.name = cast.toString(inputValue);
            } else {
                argValues[inputName] = inputValue;
            }

            i += 1;
        }

        currentStackFrame.reporting = null;
        currentStackFrame.reported = null;
        currentStackFrame.waitingReporter = false;
    }

    const start = i;

    for (; i < length; i++) {
        let lastOperation = i === length - 1;
        let opCached = ops[i];

        const blockFunction = opCached._blockFunction!;

        // Update values for arguments (inputs).
        const argValues = opCached._argValues;

        // Fields are set during opCached initialization.

        // Blocks should glow when a script is starting,
        // not after it has finished (see #1404).
        // Only blocks in blockContainers that don't forceNoGlow
        // should request a glow.
        if (!blockContainer.forceNoGlow) {
            thread.requestScriptGlowInFrame = true;
        }

        // Inputs are set during previous steps in the loop.

        const primitiveReportedValue = blockFunction(argValues, blockUtility);

        // cc - preserve returned value
        if (opCached.opcode === 'procedures_return') {
            break;
        }

        // Skip to specific opcode to implement short-circuit evaluation.
        if (blockUtility.skipToOpcode) {
            if (typeof blockUtility.skipToOpcode === 'boolean') {
                // blockUtility.skipToOpcode is true
                blockUtility.skipToOpcode = null;
                continue;
            }
            while (ops[i].opcode !== blockUtility.skipToOpcode) {
                ++i;
            }
            blockUtility.skipToOpcode = null;
            // Update current block.
            opCached = ops[i];
            lastOperation = i === length - 1;
        }

        // If it's a promise, wait until promise resolves.
        // cc - if it's procedure_call, treat it as a promise.
        const isValuePromise = isPromise(primitiveReportedValue);
        const isCaller = isProcedureCaller(opCached);
        if (isValuePromise || isCaller) {
            if (isValuePromise) {
                handlePromise(primitiveReportedValue, sequencer, thread, opCached, lastOperation);
            }

            // Store the already reported values. They will be thawed into the
            // future versions of the same operations by block id. The reporting
            // operation if it is promise waiting will set its parent value at
            // that time.
            thread.justReported = null;
            currentStackFrame.waitingReporter = isCaller;
            currentStackFrame.reporting = ops[i].id;
            currentStackFrame.reported = ops.slice(0, i).map(reportedCached => {
                const inputName = reportedCached._parentKey;
                const reportedValues = reportedCached._parentValues!;

                if (inputName === 'BROADCAST_INPUT') {
                    return {
                        opCached: reportedCached.id,
                        inputValue: reportedValues[inputName].BROADCAST_OPTION.name
                    };
                }
                return {
                    opCached: reportedCached.id,
                    inputValue: reportedValues ? reportedValues[inputName!] : null
                };
            });

            // We are waiting for a promise. Stop running this set of operations
            // and continue them later after thawing the reported values.
            break;
        } else if (thread.status === Thread.STATUS_RUNNING) {
            if (lastOperation) {
                handleReport(primitiveReportedValue, sequencer, thread, opCached, lastOperation);
            } else {
                // By definition a block that is not last in the list has a
                // parent.
                const inputName = opCached._parentKey!;
                const parentValues = opCached._parentValues!;

                if (inputName === 'BROADCAST_INPUT') {
                    // Something is plugged into the broadcast input.
                    // Cast it to a string. We don't need an id here.
                    parentValues.BROADCAST_OPTION!.id = null;
                    parentValues.BROADCAST_OPTION!.name = cast.toString(primitiveReportedValue);
                } else {
                    parentValues[inputName] = primitiveReportedValue;
                }
            }
        }
    }

    if (runtime.profiler !== null) {
        if (blockCached._profiler !== runtime.profiler) {
            _prepareBlockProfiling(runtime.profiler, blockCached);
        }
        // Determine the index that is after the last executed block. `i` is
        // currently the block that was just executed. `i + 1` will be the block
        // after that. `length` with the min call makes sure we don't try to
        // reference an operation outside of the set of operations.
        const end = Math.min(i + 1, length);
        for (let p = start; p < end; p++) {
            ops[p]._profilerFrame!.count += 1;
        }
    }
};

export default execute;
